import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Button, Icon, IconButton, Text, Textarea, Tooltip } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import {
  AdSystemT,
  AssetLineKindT,
  useActiveTextsAssetLinesQuery,
  useCreateAssetLineMutation,
  useDeleteAssetLineMutation,
} from "../../graphql/generated/graphql";
import { ModalWrapper } from "../ModalWrapper";
import { SmallLoader } from "../SmallLoader";

const MIN_PRIMARY_TEXT_LENGTH = 100;
const MAX_PRIMARY_TEXT_LENGTH = 175;
const RECOMMENDED_TEXT_LENGTH = 125;

const TextVariant = ({ onClick, tooltip, variant }: { onClick?: () => void; tooltip: string; variant: string }) => {
  return (
    <div className="text-variant-item">
      <div>
        <Text>{variant}</Text>
      </div>
      <Tooltip content={tooltip}>
        <IconButton icon="shp-plus" ghost onClick={onClick} />
      </Tooltip>
    </div>
  );
};

export interface MetaPrimaryTextHandle {
  isSaving: () => boolean;
  isValid: () => boolean;
  save: () => Promise<void>;
}

interface MetaPrimaryTextProps {
  settingsMode?: boolean;
}

export const MetaPrimaryText = forwardRef<MetaPrimaryTextHandle, MetaPrimaryTextProps>(
  ({ settingsMode = false }, ref) => {
    const { t } = useTranslation("translation", { keyPrefix: "Wizard Meta Adtext" });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [primaryText, setPrimaryText] = useState("");
    const [existingAssetLineId, setExistingAssetLineId] = useState<string | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [originalText, setOriginalText] = useState("");

    const { data, loading, refetch } = useActiveTextsAssetLinesQuery({
      variables: { adSystem: AdSystemT.MetaAdsT },
    });

    const [createAssetLine, { loading: creatingAssetLine }] = useCreateAssetLineMutation();
    const [deleteAssetLine, { loading: deletingAssetLine }] = useDeleteAssetLineMutation();

    const savingAssetLine = creatingAssetLine || deletingAssetLine;

    useEffect(() => {
      if (data?.organization?.activeTextsAssetLines) {
        const primaryTextAssetLine = data.organization.activeTextsAssetLines.find(
          (assetLine) => assetLine.kind === AssetLineKindT.PrimaryTextT
        );
        if (primaryTextAssetLine) {
          const content = primaryTextAssetLine.content || "";
          setPrimaryText(content);
          setOriginalText(content);
          setExistingAssetLineId(primaryTextAssetLine.id);
        }
      }
    }, [data]);

    const TEXT_VARIANTS = [
      t("text variant 1"),
      t("text variant 2"),
      t("text variant 3"),
      t("text variant 4"),
      t("text variant 5"),
    ];

    // Settings mode handlers
    const handleUpdateClick = () => {
      setIsEditMode(true);
    };

    const handleSaveChanges = async () => {
      try {
        // If asset line exists, delete it first before creating the new one
        if (existingAssetLineId) {
          await deleteAssetLine({
            variables: {
              id: existingAssetLineId,
            },
          });
        }

        // Create the new asset line
        await createAssetLine({
          variables: {
            content: primaryText,
            kind: AssetLineKindT.PrimaryTextT,
            adSystem: AdSystemT.MetaAdsT,
          },
        });
        await refetch();
        setOriginalText(primaryText);
        setIsEditMode(false);
      } catch (error) {
        throw error;
      }
    };

    const handleRevertChanges = () => {
      setPrimaryText(originalText);
      setIsEditMode(false);
    };

    // Expose save method and state checkers for parent components
    useImperativeHandle(ref, () => ({
      save: async () => {
        try {
          // If asset line exists, delete it first before creating the new one
          if (existingAssetLineId) {
            await deleteAssetLine({
              variables: {
                id: existingAssetLineId,
              },
            });
          }

          // Create the new asset line
          await createAssetLine({
            variables: {
              content: primaryText,
              kind: AssetLineKindT.PrimaryTextT,
              adSystem: AdSystemT.MetaAdsT,
            },
          });
          await refetch();
        } catch (error) {
          throw error;
        }
      },
      isValid: () => {
        return primaryText.length >= MIN_PRIMARY_TEXT_LENGTH && primaryText.length <= MAX_PRIMARY_TEXT_LENGTH;
      },
      isSaving: () => {
        return savingAssetLine;
      },
    }));

    const InputAssistiveText = () => {
      if (primaryText.length > 0 && primaryText.length < MIN_PRIMARY_TEXT_LENGTH) {
        return (
          <div
            className="typography-inherited-color"
            style={{ display: "flex", alignItems: "center", gap: "4px", color: "var(--app-critical)" }}
          >
            <Icon icon="shp-alert" size="sm" />
            <div>
              <Text>{t("Text too short", { recommendedTextLength: RECOMMENDED_TEXT_LENGTH })}</Text>
            </div>
          </div>
        );
      }
      if (primaryText.length > MAX_PRIMARY_TEXT_LENGTH) {
        return (
          <div
            className="typography-inherited-color"
            style={{ display: "flex", alignItems: "center", gap: "4px", color: "var(--app-critical)" }}
          >
            <Icon icon="shp-alert" size="sm" />
            <div>
              <Text>{t("Text too long", { recommendedTextLength: RECOMMENDED_TEXT_LENGTH })}</Text>
            </div>
          </div>
        );
      }
      if (primaryText.length >= MIN_PRIMARY_TEXT_LENGTH && primaryText.length <= MAX_PRIMARY_TEXT_LENGTH) {
        return (
          <div
            className="typography-inherited-color"
            style={{ display: "flex", alignItems: "center", gap: "4px", color: "var(--app-success)" }}
          >
            <Icon icon="shp-checkmark" size="sm" />
            <div>
              <Text>{t("Textarea tip", { recommendedTextLength: RECOMMENDED_TEXT_LENGTH })}</Text>
            </div>
          </div>
        );
      }
      return (
        <div>
          <Text>{t("Textarea tip", { recommendedTextLength: RECOMMENDED_TEXT_LENGTH })}</Text>
        </div>
      );
    };

    return (
      <>
        <div style={{ marginBottom: "20px" }}>
          <Trans components={{ b: <b /> }} i18nKey="Description" t={t} />
          <Text>
            <a
              href="#"
              onClick={(event) => {
                event.preventDefault();
                setIsModalOpen(true);
              }}
            >
              {t("Modal label")}
            </a>
          </Text>
        </div>
        {loading && <SmallLoader />}
        {!loading && (
          <>
            <div style={{ marginBottom: "4px" }}>
              <Text>{t("Textarea label")}</Text>
            </div>
            <div style={{ maxWidth: "600px" }}>
              {/* The bare input, not TextareaField: the caption above is the visible label
                  and TextareaField ignores `labelHidden`, so it would render a second one.
                  `label` still reaches the textarea as its accessible name. */}
              <Textarea
                disabled={settingsMode && !isEditMode}
                label={t("Textarea label")}
                name="primary-text"
                value={primaryText}
                width="full"
                onChange={setPrimaryText}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
                <InputAssistiveText />
                <div>
                  <Text>{t("Textarea length", { symbols: primaryText.length })}</Text>
                </div>
              </div>
              {(!settingsMode || isEditMode) && (
                <div
                  style={{
                    marginTop: "20px",
                    padding: "4px",
                    backgroundColor: "var(--app-surface-muted)",
                    borderRadius: "var(--app-radius-box)",
                  }}
                >
                  <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    <div>
                      <Text>{t("Recommended text variants")}</Text>
                    </div>
                    <div style={{ color: "var(--app-primary)" }}>
                      <Tooltip closeDelay={300} content={t("Recommended text variants tooltip")} delay={500}>
                        <Icon icon="shp-info" size="sm" />
                      </Tooltip>
                    </div>
                  </div>
                  {TEXT_VARIANTS.map((text) => {
                    return (
                      <TextVariant
                        key={text}
                        tooltip={t("Select variant tooltip")}
                        variant={text}
                        onClick={() => setPrimaryText(text)}
                      />
                    );
                  })}
                </div>
              )}
              {settingsMode && (
                <div style={{ display: "flex", gap: "8px", margin: "16px 0" }}>
                  {!isEditMode ? (
                    <Button variant="primary" onClick={handleUpdateClick}>
                      {t("Update")}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="action"
                        disabled={
                          primaryText.length < MIN_PRIMARY_TEXT_LENGTH ||
                          primaryText.length > MAX_PRIMARY_TEXT_LENGTH ||
                          savingAssetLine
                        }
                        onClick={handleSaveChanges}
                      >
                        {t("Save changes")}
                      </Button>
                      <Button disabled={savingAssetLine} variant="secondary" onClick={handleRevertChanges}>
                        {t("Revert changes")}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
        <ModalWrapper
          isOpen={isModalOpen}
          maxWidth={true}
          title={t("Modal title")}
          onClose={() => setIsModalOpen(false)}
        >
          <div style={{ marginBottom: "16px" }}>
            <Text>{t("Modal text 1")}</Text>
          </div>
          <Trans components={{ b: <b /> }} i18nKey="Modal list 1 title" t={t} />
          <ul style={{ margin: "0 0 16px", paddingLeft: "15px" }}>
            <li>{t("Modal list 1 item 1")}</li>
            <li>{t("Modal list 1 item 2")}</li>
            <li>{t("Modal list 1 item 3")}</li>
          </ul>
          <Trans components={{ b: <b /> }} i18nKey="Modal list 2 title" t={t} />
          <ul style={{ margin: "0", paddingLeft: "15px" }}>
            <li>{t("Modal list 2 item 1")}</li>
            <li>{t("Modal list 2 item 2")}</li>
            <li>{t("Modal list 2 item 3")}</li>
          </ul>
        </ModalWrapper>
      </>
    );
  }
);
